import { useNavigate } from 'react-router';
import { useFinanceOverview } from '@/application/usecases/useFinance';
import { failureWords } from '@/shared/failure/failure';
import { strings } from '@/shared/strings';
import { useMoney } from '@/ui/money/moneyView';
import { SkeletonRows } from '@/ui/feedback/Loading';
import { Button } from '@/ui/primitives/Button';
import { FinanceFigures, type OverviewList } from '@/features/payments/FinanceOverview';
import { doctorStatementPath } from '@/features/payments/paymentPaths';
import { WidgetSection } from '../WidgetSection';
import { WIDGET_ROWS } from './caseLists';
import board from '../WidgetBoard.module.css';

const text = strings.dashboard;

/** The list of Payments a figure opens, as that screen reads it from its address. */
function paymentsList(list: OverviewList): string {
  return list === 'unpaid' ? '/payments' : `/payments?list=${list}`;
}

/**
 * What the lab is owed: the four figures over the lists of Payments, and under
 * them what is outstanding by how long. The request of 8 October 2026
 * (fourth), drawn here as that screen draws it, from the same request.
 *
 * Each figure opens the list of Payments that holds what it counts. No card
 * reads as pressed: none of those lists is on this screen.
 */
export function MoneyOwedWidget() {
  const navigate = useNavigate();

  return (
    <WidgetSection
      label={text.kicker.money}
      title={text.widgets.moneyOwed.title}
      action={
        <Button variant="text" onClick={() => void navigate('/payments')}>
          {text.viewAll}
        </Button>
      }
    >
      <FinanceFigures
        current=""
        onShow={(list) => void navigate(paymentsList(list))}
        // Each age opens To be paid narrowed to it. The lab's request of 17 October 2026, item 1.
        onShowAge={(age) => void navigate(`/payments?age=${age}`)}
      />
    </WidgetSection>
  );
}

/**
 * The doctors who owe the most, most first, as By doctor on Payments lists
 * them: the server's order. A row opens the doctor's statement.
 */
export function OwedByDoctorWidget() {
  const navigate = useNavigate();
  const overview = useFinanceOverview();
  const money = useMoney();
  const title = text.widgets.owedByDoctor.title;

  // A doctor who owes nothing is on the full list for the credit they hold, not here.
  const owing = (overview.data?.doctors ?? []).filter((doctor) => doctor.outstanding > 0);
  const rows = owing.slice(0, WIDGET_ROWS);

  return (
    <WidgetSection
      label={text.kicker.money}
      title={title}
      action={
        <Button variant="text" onClick={() => void navigate(paymentsList('doctors'))}>
          {text.viewAll}
        </Button>
      }
    >
      {overview.isPending && <SkeletonRows rows={3} label={strings.common.loadingOrders} />}
      {overview.isError && (
        <p className={board.quiet}>{failureWords(overview.error, strings.errors.server)}</p>
      )}

      {overview.data !== undefined &&
        (rows.length === 0 ? (
          <p className={board.quiet}>{text.empty.owedByDoctor}</p>
        ) : (
          <>
            <ul className={board.rows} aria-label={title}>
              {rows.map((doctor) => (
                <li key={doctor.doctorId}>
                  <button
                    type="button"
                    className={board.row}
                    onClick={() => void navigate(doctorStatementPath(doctor.doctorId))}
                  >
                    <span className={board.what}>
                      <span className={board.mono}>{doctor.code}</span>
                      <span dir="auto">{doctor.name ?? strings.table.hidden}</span>
                    </span>
                    <span className={board.who}>{text.casesOwing(doctor.cases)}</span>
                    <span className={board.end}>{money(doctor.outstanding)}</span>
                  </button>
                </li>
              ))}
            </ul>
            {owing.length > rows.length && (
              <p className={board.more}>{text.more(rows.length, owing.length)}</p>
            )}
          </>
        ))}
    </WidgetSection>
  );
}
